import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { useToast } from '../hooks/use-toast';
import { Loader2, Calendar, MapPin, CheckCircle2, AlertTriangle, CalendarPlus, CalendarDays, Check, ChevronsUpDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVINCES = [
  'Ciudad Autónoma de Buenos Aires',
  'Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán'
];

const HeroWithForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [provincePopoverOpen, setProvincePopoverOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const eventDate = new Date('2026-09-03T10:00:00-03:00').getTime();
    const update = () => {
      const diff = eventDate - Date.now();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000)
      });
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    province: '',
    timeSlot: '',
    whatsapp: '+54 9 ',
    dniId: '',
    email: '',
    dietaryRestrictions: '',
    dietaryDetails: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (field, value) => {
    if (field === 'whatsapp') {
      if (!value.startsWith('+54 9 ')) {
        value = '+54 9 ' + value.replace(/^\+54\s?9?\s?/, '');
      }
    }
    if (field === 'dniId') {
      // Only letters and numbers, no dots or hyphens
      value = value.replace(/[^a-zA-Z0-9]/g, '');
    }
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: false });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let firstErrorField = null;

    if (!formData.fullName.trim()) {
      newErrors.fullName = true;
      if (!firstErrorField) firstErrorField = 'fullName';
    }
    if (!formData.company.trim()) {
      newErrors.company = true;
      if (!firstErrorField) firstErrorField = 'company';
    }
    if (!formData.province.trim()) {
      newErrors.province = true;
      if (!firstErrorField) firstErrorField = 'province';
    }
    if (!formData.timeSlot) {
      newErrors.timeSlot = true;
      if (!firstErrorField) firstErrorField = 'timeSlot';
    }
    if (!formData.whatsapp || formData.whatsapp.trim() === '+54 9' || formData.whatsapp.trim() === '+54 9 ') {
      newErrors.whatsapp = true;
      if (!firstErrorField) firstErrorField = 'whatsapp';
    }
    if (!formData.dniId.trim()) {
      newErrors.dniId = true;
      if (!firstErrorField) firstErrorField = 'dniId';
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = true;
      if (!firstErrorField) firstErrorField = 'email';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: 'Faltan datos',
        description: 'Por favor completa todos los campos obligatorios marcados en rojo.',
        variant: 'destructive'
      });
      
      // Scroll to first error field
      setTimeout(() => {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return false;
    }
    return true;
  };

  const submitRegistration = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/register`, formData);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        fullName: '',
        company: '',
        province: '',
        timeSlot: '',
        whatsapp: '+54 9 ',
        dniId: '',
        email: '',
        dietaryRestrictions: '',
        dietaryDetails: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Hubo un problema al enviar tu registro. Por favor intenta nuevamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Check for duplicate registration first
      const checkResponse = await axios.post(`${API}/check-duplicate`, {
        fullName: formData.fullName,
        company: formData.company,
        email: formData.email
      });

      if (checkResponse.data.exists) {
        // Show duplicate confirmation modal
        setIsSubmitting(false);
        setShowDuplicateModal(true);
        return;
      }

      // No duplicate, submit directly
      setIsSubmitting(false);
      await submitRegistration();
    } catch (error) {
      console.error('Error checking duplicate:', error);
      // If check fails, submit anyway
      setIsSubmitting(false);
      await submitRegistration();
    }
  };

  const handleConfirmResend = async () => {
    setShowDuplicateModal(false);
    await submitRegistration();
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden hero-bg-orb">
      {/* Decorative orbs (parallax) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-20 left-10 w-96 h-96 bg-yellow-300/30 rounded-full blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        ></div>
        <div 
          className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-fuchsia-500/20 rounded-full blur-3xl"
          style={{ transform: `translateY(${scrollY * -0.2}px)` }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left side - Hero content */}
          <div className="text-white">
            <h1 className="mb-8 leading-[1.1] space-y-1 sm:space-y-2">
              <span 
                className="block font-wide-bold text-white whitespace-nowrap"
                style={{ fontSize: 'clamp(1.2rem, 1.85vw, 1.75rem)' }}
              >
                Nuevas ideas
              </span>
              <span 
                className="block font-wide-bold text-white whitespace-nowrap"
                style={{ fontSize: 'clamp(1.2rem, 1.85vw, 1.75rem)' }}
              >
                Nuevas conexiones
              </span>
              <span 
                className="block font-wide-bold text-white whitespace-nowrap"
                style={{ fontSize: 'clamp(1.2rem, 1.85vw, 1.75rem)' }}
              >
                Nuevas oportunidades
              </span>
            </h1>

            <p className="text-base sm:text-lg text-white/95 mb-6 leading-relaxed max-w-xl">
              El encuentro donde más de 40 marcas de tecnología se reúnen con canales, para generar negocios, networking y nuevas oportunidades comerciales.
            </p>

            <div className="space-y-2 mb-8 text-white">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-yellow-200 flex-shrink-0" />
                <span className="font-semibold">3 de Septiembre 2026 — El Cubo Eventos (Vicente López, Bs. As.)</span>
              </div>
              <p className="text-sm text-white/90 ml-7">
                Cupos limitados, reservá tu lugar. Exclusivo para clientes de Elit S.A.
              </p>
            </div>

            {/* Countdown */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 max-w-md">
              {[
                { value: countdown.days, label: 'Días' },
                { value: countdown.hours, label: 'Horas' },
                { value: countdown.minutes, label: 'Min' },
                { value: countdown.seconds, label: 'Seg' }
              ].map((unit, idx) => (
                <div key={idx} className="bg-white/15 backdrop-blur-md border border-white/30 rounded-xl px-2 py-3 text-center shadow-lg">
                  <div className="font-wide text-2xl sm:text-3xl lg:text-4xl text-white leading-none tabular-nums">
                    {String(unit.value).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] sm:text-xs font-semibold text-white/90 uppercase tracking-wider mt-1">
                    {unit.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Registration form */}
          <div>
            <Card className="border-orange-200 shadow-2xl bg-white">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">
                  Reservá tu lugar
                </h2>
                <p className="text-slate-600 mb-6 text-center">
                  Completá el formulario para confirmar tu asistencia
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div>
                    <Label htmlFor="fullName" className={errors.fullName ? "text-red-600 font-semibold" : "text-slate-700"}>
                      Nombre y apellido *
                    </Label>
                    <Input
                      id="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      className={`mt-1 transition-all ${
                        errors.fullName 
                          ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                          : 'focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="Juan Pérez"
                    />
                    {errors.fullName && <p className="text-red-600 text-xs mt-1">Este campo es obligatorio</p>}
                  </div>

                  <div>
                    <Label htmlFor="company" className={errors.company ? "text-red-600 font-semibold" : "text-slate-700"}>
                      Razón social *
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      className={`mt-1 transition-all ${
                        errors.company 
                          ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                          : 'focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="Empresa SA"
                    />
                    {errors.company && <p className="text-red-600 text-xs mt-1">Este campo es obligatorio</p>}
                  </div>

                  <div>
                    <Label htmlFor="province" className={errors.province ? "text-red-600 font-semibold" : "text-slate-700"}>
                      Provincia *
                    </Label>
                    <Popover open={provincePopoverOpen} onOpenChange={setProvincePopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          id="province"
                          type="button"
                          className={`mt-1 w-full flex items-center justify-between rounded-md border bg-white px-3 py-2 text-sm transition-all ${
                            errors.province 
                              ? 'border-red-500 ring-2 ring-red-200' 
                              : 'border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
                          } ${!formData.province ? 'text-slate-500' : 'text-slate-900'}`}
                        >
                          {formData.province || 'Selecciona tu provincia'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Buscar provincia..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>No se encontró la provincia.</CommandEmpty>
                            <CommandGroup>
                              {PROVINCES.map((prov) => (
                                <CommandItem
                                  key={prov}
                                  value={prov}
                                  onSelect={() => {
                                    handleChange('province', prov);
                                    setProvincePopoverOpen(false);
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      formData.province === prov ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                  {prov}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {errors.province && <p className="text-red-600 text-xs mt-1">Este campo es obligatorio</p>}
                  </div>

                  <div>
                    <Label htmlFor="timeSlot" className={errors.timeSlot ? "text-red-600 font-semibold" : "text-slate-700"}>
                      Horario que asistiré *
                    </Label>
                    <Select value={formData.timeSlot} onValueChange={(value) => handleChange('timeSlot', value)}>
                      <SelectTrigger 
                        id="timeSlot"
                        className={`mt-1 transition-all ${
                          errors.timeSlot 
                            ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                            : 'focus:ring-orange-500 focus:border-orange-500'
                        }`}
                      >
                        <SelectValue placeholder="Selecciona un horario" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Desde las 9:30 Hs">Desde las 9:30 Hs</SelectItem>
                        <SelectItem value="Desde las 13:00 Hs">Desde las 13:00 Hs</SelectItem>
                        <SelectItem value="Desde las 16 Hs">Desde las 16 Hs</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.timeSlot && <p className="text-red-600 text-xs mt-1">Por favor selecciona un horario</p>}
                  </div>

                  <div>
                    <Label htmlFor="whatsapp" className={errors.whatsapp ? "text-red-600 font-semibold" : "text-slate-700"}>
                      WhatsApp *
                    </Label>
                    <Input
                      id="whatsapp"
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => handleChange('whatsapp', e.target.value)}
                      className={`mt-1 transition-all ${
                        errors.whatsapp 
                          ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                          : 'focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="+54 9 XXX XXX XXXX"
                    />
                    {errors.whatsapp && <p className="text-red-600 text-xs mt-1">Por favor ingresa tu número de WhatsApp</p>}
                  </div>

                  <div>
                    <Label htmlFor="dniId" className={errors.dniId ? "text-red-600 font-semibold" : "text-slate-700"}>
                      DNI / ID *
                    </Label>
                    <Input
                      id="dniId"
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      value={formData.dniId}
                      onChange={(e) => handleChange('dniId', e.target.value)}
                      className={`mt-1 transition-all ${
                        errors.dniId 
                          ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                          : 'focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="Solo números y letras"
                    />
                    {errors.dniId && <p className="text-red-600 text-xs mt-1">Este campo es obligatorio</p>}
                  </div>

                  <div>
                    <Label htmlFor="email" className={errors.email ? "text-red-600 font-semibold" : "text-slate-700"}>
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`mt-1 transition-all ${
                        errors.email 
                          ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500' 
                          : 'focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="ejemplo@empresa.com"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1">Por favor ingresa un email válido</p>}
                  </div>

                  <div>
                    <Label htmlFor="dietaryRestrictions" className="text-slate-700">Restricciones alimentarias</Label>
                    <Select value={formData.dietaryRestrictions} onValueChange={(value) => handleChange('dietaryRestrictions', value)}>
                      <SelectTrigger className="mt-1 focus:ring-orange-500 focus:border-orange-500">
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ninguna">Ninguna</SelectItem>
                        <SelectItem value="Vegetariano">Vegetariano</SelectItem>
                        <SelectItem value="Vegano">Vegano</SelectItem>
                        <SelectItem value="Celíaco">Celíaco</SelectItem>
                        <SelectItem value="Alérgico">Alérgico (especificar)</SelectItem>
                        <SelectItem value="Otro">Otro (especificar)</SelectItem>
                      </SelectContent>
                    </Select>
                    {(formData.dietaryRestrictions === 'Alérgico' || formData.dietaryRestrictions === 'Otro') && (
                      <Input
                        id="dietaryDetails"
                        type="text"
                        value={formData.dietaryDetails}
                        onChange={(e) => handleChange('dietaryDetails', e.target.value)}
                        className="mt-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder={formData.dietaryRestrictions === 'Alérgico' ? 'Especifica tu alergia' : 'Especifica tu restricción'}
                      />
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 mt-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Reservar mi lugar'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none max-h-[90vh] overflow-y-auto">
          <div className="bg-white">
            {/* Success header with gradient */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-lg">
                <CheckCircle2 className="w-12 h-12 text-green-500" strokeWidth={2.5} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Registro exitoso
              </h2>
            </div>

            {/* Body content */}
            <div className="p-8 space-y-5">
              <p className="text-lg text-slate-700 text-center leading-relaxed">
                Te esperamos en el rango horario que indicaste.
              </p>

              <div className="flex items-start gap-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-800 leading-relaxed">
                  Es muy importante tu presencia. En caso de no poder asistir, comunicate con tu ejecutivo comercial.
                </p>
              </div>

              {/* Add to Calendar Section */}
              <div className="border-t border-slate-200 pt-5">
                <p className="text-sm font-semibold text-slate-700 text-center mb-3 flex items-center justify-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-orange-600" />
                  Agregar al calendario
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href="https://calendar.google.com/calendar/render?action=TEMPLATE&text=Elit+Brand+Tour+-+Buenos+Aires&dates=20260903T123000Z/20260903T220000Z&details=Elit+Brand+Tour+Buenos+Aires.+Nuevas+ideas%2C+nuevas+conexiones%2C+nuevas+oportunidades.&location=El+Cubo+Eventos%2C+Vicente+L%C3%B3pez%2C+Buenos+Aires"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-slate-300 hover:border-orange-500 hover:bg-orange-50 text-slate-700 py-3 px-4 rounded-full text-sm font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 48 48" fill="none">
                      <path d="M37 6H11a5 5 0 00-5 5v26a5 5 0 005 5h26a5 5 0 005-5V11a5 5 0 00-5-5z" fill="#fff" stroke="#4285f4" strokeWidth="2"/>
                      <path d="M19 24h10M24 19v10" stroke="#4285f4" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                    Google
                  </a>
                  <a
                    href="/elit-brand-tour.ics"
                    download="elit-brand-tour.ics"
                    className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-slate-300 hover:border-orange-500 hover:bg-orange-50 text-slate-700 py-3 px-4 rounded-full text-sm font-medium transition-all duration-300"
                  >
                    <CalendarDays className="w-4 h-4 text-slate-700" />
                    Apple / Otros
                  </a>
                </div>
                <p className="text-xs text-slate-500 text-center mt-2">
                  3 de Septiembre 2026 · 9:30 a 19:00 hs
                </p>
              </div>

              <Button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-5 text-base rounded-full transition-all duration-300 mt-4"
              >
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Registration Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none">
          <div className="bg-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-lg">
                <AlertTriangle className="w-12 h-12 text-amber-500" strokeWidth={2.5} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Asistente previamente registrado
              </h2>
            </div>

            {/* Body */}
            <div className="p-8 space-y-5">
              <p className="text-base text-slate-700 text-center leading-relaxed">
                Ya existe un registro con los mismos datos.
              </p>
              <p className="text-lg text-slate-900 text-center font-semibold leading-relaxed">
                ¿Deseás enviar nuevamente este formulario?
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={() => setShowDuplicateModal(false)}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-900 py-5 text-base rounded-full transition-all duration-300"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmResend}
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white py-5 text-base rounded-full transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Sí, enviar de nuevo'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HeroWithForm;
